import type {
  AppConfig,
  CreateSubscriptionResult,
  Credentials,
  CustomerInput,
  CustomerProfile,
  Plan,
  PricingResponse,
} from './types';

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') || '';

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      ...init,
    });
  } catch {
    throw new ApiError(0, 'network_error', 'Could not reach the checkout server. Is it running?');
  }

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    const err = body?.error;
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? `Request failed (${res.status})`);
  }
  return body as T;
}

export const api = {
  getConfig: () => request<AppConfig>('/api/config'),
  getPricing: () => request<PricingResponse>('/api/pricing'),
  getCustomer: (id: string) =>
    request<CustomerProfile>(`/api/customers/${encodeURIComponent(id)}`),
  createSubscription: (payload: {
    plan: Plan;
    customerId?: string;
    customer?: CustomerInput;
    credentials?: Credentials;
  }) =>
    request<CreateSubscriptionResult>('/api/subscriptions', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};
